import { APP_BUILD } from "../config/buildInfo";
import { VIDEO_SERVER_BASE_URL } from "../config/videoServer";
import { detectWebViewMode } from "../utils/webViewMode";

export type DesktopCachePolicy = { enabled: boolean; capacityGB: number; prefetchTracks?: number; adaptivePrefetch?: boolean };
let accountCache: { accountId: string | null; source: string; policy: DesktopCachePolicy } | null = null;
let effectiveCapacity = Number.MAX_SAFE_INTEGER;
const DEVICE_POLICY_KEY = "kirkwat-desktop-cache-policy-v2";
const DEVICE_DEFAULT_POLICY: DesktopCachePolicy = { enabled: true, capacityGB: 0, prefetchTracks: 3, adaptivePrefetch: true };
function readDevicePolicy(): DesktopCachePolicy {
  try {
    const saved = JSON.parse(window.localStorage.getItem(DEVICE_POLICY_KEY) || "null");
    if (saved && Number.isFinite(saved.capacityGB) && (saved.capacityGB === 0 || saved.capacityGB >= 0.25)) {
      return { ...DEVICE_DEFAULT_POLICY, ...saved, enabled: saved.enabled !== false };
    }
    const legacy = Number(window.localStorage.getItem("kirkwat-desktop-cache-capacity-v1"));
    if (Number.isFinite(legacy) && legacy >= 0.25) return { ...DEVICE_DEFAULT_POLICY, capacityGB: legacy };
  } catch {}
  return { ...DEVICE_DEFAULT_POLICY };
}
async function policyCapacity(policy: DesktopCachePolicy) {
  let capacity = policy.capacityGB === 0 ? Number.MAX_SAFE_INTEGER : policy.capacityGB * 1024 ** 3;
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota) capacity = Math.min(capacity, Math.floor(estimate.quota * 0.9));
  } catch {}
  return capacity;
}
function publishPolicy(profile: NonNullable<typeof accountCache>, capacity: number) {
  const changed = JSON.stringify(profile) !== JSON.stringify(accountCache) || capacity !== effectiveCapacity;
  accountCache = profile;
  effectiveCapacity = capacity;
  sendCacheSettings();
  if (changed) window.dispatchEvent(new Event("playback-cache-policy-changed"));
}
let policyRequest = 0;
const desktopCache = () => typeof window !== "undefined" && detectWebViewMode() === "desktop";
const PREFETCH_METRICS_KEY = "kirkwat-prefetch-metrics-v1";
const PREFETCH_URLS_KEY = "kirkwat-prefetch-urls-v1";
const GUEST_PREFETCH_LEVEL_KEY = "kirkwat-desktop-prefetch-level-v1";
const BACKGROUND_CACHE_ORIGIN = "https://seaking1.b-cdn.net";

export function backgroundCacheUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    const pathname = url.pathname;
    const markers = ["/videos/", "/originals/"];
    const marker = markers
      .map((candidate) => pathname.toLowerCase().lastIndexOf(candidate))
      .filter((index) => index >= 0)
      .sort((left, right) => right - left)[0];
    if (marker === undefined) return null;
    return `${BACKGROUND_CACHE_ORIGIN}${pathname.slice(marker)}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function guestPrefetchLevel(fallback: number) {
  if (typeof window === "undefined") return fallback;
  try {
    const value = Number(window.localStorage.getItem(GUEST_PREFETCH_LEVEL_KEY));
    return Number.isFinite(value) ? Math.max(0, Math.min(10, Math.round(value))) : fallback;
  } catch {
    return fallback;
  }
}

export function getDesktopCachePolicy() {
  const profile = accountCache || { accountId: null, source: "device", policy: readDevicePolicy() };
  if (profile.accountId) return profile;
  return { ...profile, policy: { ...profile.policy,
    prefetchTracks: guestPrefetchLevel(profile.policy.prefetchTracks ?? 3) } };
}

export async function saveDesktopPrefetchTracks(value: number) {
  const prefetchTracks = Math.max(0, Math.min(10, Math.round(Number(value) || 0)));
  const profile = getDesktopCachePolicy();
  if (profile.accountId) {
    await saveDesktopCachePolicy({ ...profile.policy, prefetchTracks });
    return getDesktopCachePolicy();
  }
  try { window.localStorage.setItem(GUEST_PREFETCH_LEVEL_KEY, String(prefetchTracks)); } catch {}
  window.dispatchEvent(new Event("playback-cache-policy-changed"));
  return getDesktopCachePolicy();
}

export async function refreshPlaybackCachePolicy() {
  if (typeof window === "undefined") return;
  const sequence = ++policyRequest;
  let profile = accountCache || { accountId: null, source: "device", policy: readDevicePolicy() };
  try {
    const response = await fetch(`${VIDEO_SERVER_BASE_URL}/api/playback/cache-policy`, {
      credentials: "include", cache: "no-store",
    });
    if (response.ok) {
      const remote = await response.json();
      const size = remote?.policy?.capacityGB;
      if (!remote?.policy || !Number.isFinite(size) || (size !== 0 && size < 0.25)) throw new Error("无效缓存配置");
      profile = remote.accountId ? remote : { accountId: null, source: "device", policy: readDevicePolicy() };
    } else if (response.status === 404 || response.status === 501) {
      profile = { accountId: null, source: "device", policy: readDevicePolicy() };
    }
  } catch { /* Keep last known owner on transient network errors. */ }
  const capacity = await policyCapacity(profile.policy);
  if (sequence !== policyRequest) return;
  publishPolicy(profile, capacity);
}

export function suspendPlaybackCache() {
  policyRequest += 1;
  accountCache = null;
  sendCacheSettings();
}

function readPrefetchState() {
  if (typeof window === "undefined") return { attempts: 0, hits: 0 };
  try { return { attempts: 0, hits: 0, ...JSON.parse(localStorage.getItem(PREFETCH_METRICS_KEY) || "{}")} } catch { return { attempts: 0, hits: 0 }; }
}
function savePrefetchState(value: { attempts: number; hits: number }) {
  try { localStorage.setItem(PREFETCH_METRICS_KEY, JSON.stringify(value)); } catch {}
}
function rememberPrefetchUrls(urls: string[]) {
  try {
    const current = JSON.parse(localStorage.getItem(PREFETCH_URLS_KEY) || "{}");
    const now = Date.now();
    Object.keys(current).forEach((url) => { if (now - Number(current[url]) > 24 * 60 * 60 * 1000) delete current[url]; });
    urls.forEach((url) => { current[url] = now; });
    localStorage.setItem(PREFETCH_URLS_KEY, JSON.stringify(current));
  } catch {}
}
export function recordPrefetchPlayback(url: string, source: string) {
  if (typeof window === "undefined") return;
  try {
    const remembered = JSON.parse(localStorage.getItem(PREFETCH_URLS_KEY) || "{}");
    if (!remembered[url]) return;
    delete remembered[url];
    localStorage.setItem(PREFETCH_URLS_KEY, JSON.stringify(remembered));
    const metrics = readPrefetchState();
    metrics.attempts = Math.max(0, Number(metrics.attempts) || 0);
    metrics.hits = Math.max(0, Number(metrics.hits) || 0);
    if (source === "cache") metrics.hits += 1;
    savePrefetchState(metrics);
  } catch {}
}
export function adaptivePrefetchCount(policy: DesktopCachePolicy, requested: number) {
  const target = Math.max(0, Math.min(10, Math.round(Number(requested) || 0)));
  if (!policy.adaptivePrefetch || target === 0 || target === 10) return target;
  const metrics = readPrefetchState();
  if (metrics.attempts < 5) return target;
  const hitRate = metrics.hits / metrics.attempts;
  if (hitRate < 0.3) return Math.max(0, target - 1);
  if (hitRate > 0.7) return Math.min(10, target + 1);
  return target;
}
async function hlsSegments(url: string): Promise<string[]> {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      priority: "low",
    } as RequestInit & { priority: "low" });
    if (!response.ok) return [];
    const playlist = await response.text();
    const lines = playlist.split(/\r?\n/).map((line) => line.trim()).filter((line) => line && !line.startsWith("#"));
    const children = lines.filter((line) => /\.m3u8(?:$|[?#])/i.test(line));
    if (children.length) return hlsSegments(new URL(children[0], url).toString());
    const init = [...playlist.matchAll(/#EXT-X-MAP:[^\r\n]*URI="([^"]+)"/gi)].map((match) => match[1]);
    return [...new Set([...init, ...lines])].map((line) => new URL(line, url).toString());
  } catch { return []; }
}

function standaloneMediaUrl(url: string) {
  try {
    const value = new URL(url);
    return !/\/videos\/hls\//i.test(value.pathname) &&
      /\.(?:mp4|webm|m4v|mov)$/i.test(value.pathname);
  } catch { return false; }
}

async function prefetchAttempt(worker: ServiceWorker, url: string, index: number) {
  return new Promise<{ stored: boolean; deferred: boolean }>((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve({ stored: false, deferred: false }), 30 * 60 * 1000);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      resolve({ stored: event.data?.stored === true, deferred: event.data?.deferred === true });
    };
    worker.postMessage({
      type: "HLS_PREFETCH",
      url,
      job: `${Date.now()}-${index}`,
      chunked: standaloneMediaUrl(url),
    }, [channel.port2]);
  });
}

async function prefetchOne(worker: ServiceWorker, url: string, index: number) {
  for (;;) {
    const result = await prefetchAttempt(worker, url, index);
    if (!result.deferred) return result.stored;
    // Foreground playback owns all media bandwidth. Resume this low-priority
    // candidate only after the worker reports that the foreground is quiet.
    await new Promise(resolve => window.setTimeout(resolve, 5000));
  }
}

export async function prefetchMediaUrls(urls: string[]) {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const policy = getDesktopCachePolicy().policy;
  if (!policy.enabled || !desktopCache()) return;
  const count = adaptivePrefetchCount(policy, policy.prefetchTracks ?? 3);
  if (count === 0) return;
  const candidates = [...new Set(urls.map(backgroundCacheUrl).filter(Boolean) as string[])];
  const expanded: string[][] = [];
  let candidateCursor = 0;
  async function expandWorker() {
    while (candidateCursor < candidates.length) {
      const url = candidates[candidateCursor++];
      expanded.push(
        /\.m3u8(?:$|[?#])/i.test(url)
          ? await hlsSegments(url)
          : /\.(?:mp4|webm|m4v|mov)(?:$|[?#])/i.test(url)
            ? [url]
            : [],
      );
    }
  }
  await Promise.all([expandWorker(), expandWorker(), expandWorker()]);
  const targets = [...new Set(expanded.flat())];
  if (!targets.length) return;
  await navigator.serviceWorker.ready.catch(() => {});
  const worker = navigator.serviceWorker.controller;
  if (!worker) return;
  const stored: string[] = [];
  for (let index = 0; index < targets.length; index += 3) {
    const batch = targets.slice(index, index + 3);
    const results = await Promise.all(
      batch.map((url, offset) => prefetchOne(worker, url, index + offset)),
    );
    results.forEach((success, offset) => {
      if (success) stored.push(batch[offset]);
    });
  }
  if (!stored.length) return;
  const metrics = readPrefetchState();
  metrics.attempts += stored.length;
  savePrefetchState(metrics);
  rememberPrefetchUrls(stored);
}

export async function cacheCompletedHlsVideo(url: string): Promise<void> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !/\.m3u8(?:$|[?#])/i.test(url)
  ) {
    return;
  }
  const settings = getHlsCacheSettings();
  if (!settings.enabled || (desktopCache() && !settings.desktopEnabled)) return;
  const registration = await navigator.serviceWorker.ready;
  sendCacheSettings();
  const worker = navigator.serviceWorker.controller || registration.active;
  const backgroundUrl = backgroundCacheUrl(url);
  if (!backgroundUrl) return;
  worker?.postMessage({
    type: "HLS_CACHE_COMPLETE",
    url: backgroundUrl,
    job: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
}

export async function cacheHlsPlaybackCheckpoint(
  url: string,
  progress: number,
): Promise<void> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !/\.m3u8(?:$|[?#])/i.test(url)
  ) {
    return;
  }
  const normalizedProgress = Math.max(0, Math.min(1, Number(progress) || 0));
  if (normalizedProgress < 0.1) return;
  const settings = getHlsCacheSettings();
  if (!settings.enabled || (desktopCache() && !settings.desktopEnabled)) return;
  const registration = await navigator.serviceWorker.ready;
  sendCacheSettings();
  const worker = navigator.serviceWorker.controller || registration.active;
  const backgroundUrl = backgroundCacheUrl(url);
  if (!backgroundUrl) return;
  worker?.postMessage({
    type: "HLS_CACHE_CHECKPOINT",
    url: backgroundUrl,
    progress: normalizedProgress,
  });
}

export async function saveDesktopCachePolicy(policy: DesktopCachePolicy | null) {
  if (policy && (!Number.isFinite(policy.capacityGB) || (policy.capacityGB !== 0 && policy.capacityGB < 0.25))) {
    throw new Error("容量须为 0（不设上限）或不小于 0.25 GB");
  }
  if (!accountCache?.accountId) {
    const next = policy ? { ...readDevicePolicy(), ...policy } : { ...DEVICE_DEFAULT_POLICY };
    try {
      const encoded = JSON.stringify(next);
      window.localStorage.setItem(DEVICE_POLICY_KEY, encoded);
      if (window.localStorage.getItem(DEVICE_POLICY_KEY) !== encoded) throw new Error("写入失败");
    } catch { throw new Error("浏览器不允许保存本机配置，请检查网站存储权限"); }
    const sequence = ++policyRequest;
    const capacity = await policyCapacity(next);
    if (sequence === policyRequest) publishPolicy({ accountId: null, source: "device", policy: next }, capacity);
    return;
  }
  const response = await fetch(`${VIDEO_SERVER_BASE_URL}/api/playback/cache-policy`, {
    method: policy ? "PUT" : "DELETE", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: policy ? JSON.stringify(policy) : undefined,
  });
  if (!response.ok) throw new Error((await response.json()).error || "缓存设置保存失败");
  await refreshPlaybackCachePolicy();
}

function sendCacheSettings() {
  postToWorker({ type: "HLS_CACHE_SETTINGS", owner: accountCache?.accountId || "guest",
    desktop: desktopCache(), settings: accountCache ? getHlsCacheSettings() : { enabled: false } });
}

export function watchPlaybackCachePolicy() {
  const refresh = () => { void refreshPlaybackCachePolicy().catch(() => {}); };
  const requested = (event: MessageEvent) => {
    if (event.data?.type === "HLS_CACHE_CONTEXT_NEEDED") sendCacheSettings();
    if (event.data?.type === "HLS_PLAYBACK_SOURCE") {
      recordPrefetchPlayback(String(event.data.url || ""), String(event.data.source || ""));
    }
  };
  refresh();
  const timer = window.setInterval(refresh, 30000);
  window.addEventListener("focus", refresh);
  navigator.serviceWorker?.addEventListener("controllerchange", sendCacheSettings);
  navigator.serviceWorker?.addEventListener("message", requested);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("focus", refresh);
    navigator.serviceWorker?.removeEventListener("controllerchange", sendCacheSettings);
    navigator.serviceWorker?.removeEventListener("message", requested);
  };
}

export type HlsCacheStrategy = "watched" | "balanced" | "aggressive";

export interface HlsCacheSettings {
  enabled: boolean;
  desktopEnabled: boolean;
  maxSegments: number;
  capacityMode: "auto" | "manual";
  maxCacheBytes: number;
  strategy: HlsCacheStrategy;
  preloadSegments: number;
  resumePlayback: boolean;
}

export interface HlsCacheStats {
  playlists: number;
  segments: number;
  bytes: number;
  videos?: number;
}

const SETTINGS_KEY = "kirkwat-hls-cache-settings-v1";
const RESUME_PREFIX = "kirkwat-playback-resume:";

export const DEFAULT_HLS_CACHE_SETTINGS: HlsCacheSettings = {
  enabled: true,
  desktopEnabled: true,
  maxSegments: 192,
  capacityMode: "auto",
  maxCacheBytes: 100 * 1024 * 1024,
  strategy: "balanced",
  preloadSegments: 3,
  resumePlayback: true,
};

function normalizeSettings(value: Partial<HlsCacheSettings> | null | undefined): HlsCacheSettings {
  const strategy =
    value?.strategy === "watched" ||
    value?.strategy === "aggressive" ||
    value?.strategy === "balanced"
      ? value.strategy
      : DEFAULT_HLS_CACHE_SETTINGS.strategy;
  return {
    enabled: value?.enabled !== false,
    desktopEnabled: value?.desktopEnabled !== false,
    maxSegments: Math.max(
      24,
      Math.min(960, Number(value?.maxSegments) || DEFAULT_HLS_CACHE_SETTINGS.maxSegments),
    ),
    capacityMode: value?.capacityMode === "manual" ? "manual" : "auto",
    maxCacheBytes: Math.max(
      25 * 1024 * 1024,
      Math.min(
        Number.MAX_SAFE_INTEGER,
        Number(value?.maxCacheBytes) || DEFAULT_HLS_CACHE_SETTINGS.maxCacheBytes,
      ),
    ),
    strategy,
    preloadSegments: Math.max(
      0,
      Math.min(24, Number(value?.preloadSegments) || DEFAULT_HLS_CACHE_SETTINGS.preloadSegments),
    ),
    resumePlayback: value?.resumePlayback !== false,
  };
}

export function getHlsCacheSettings(): HlsCacheSettings {
  if (desktopCache()) {
    const profile = getDesktopCachePolicy();
    return { ...DEFAULT_HLS_CACHE_SETTINGS, enabled: profile.policy.enabled, desktopEnabled: true,
      capacityMode: "manual", maxCacheBytes: effectiveCapacity, maxSegments: 1000000 };
  }
  if (typeof window === "undefined") return DEFAULT_HLS_CACHE_SETTINGS;
  try {
    return normalizeSettings(JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "null"));
  } catch {
    return DEFAULT_HLS_CACHE_SETTINGS;
  }
}

function postToWorker(message: unknown): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.controller?.postMessage(message);
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage(message))
    .catch(() => {});
}

export function saveHlsCacheSettings(settings: HlsCacheSettings): HlsCacheSettings {
  const normalized = normalizeSettings(settings);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(normalized));
  }
  sendCacheSettings();
  return normalized;
}

export async function applyAutomaticCacheCapacity(
  settings = getHlsCacheSettings(),
): Promise<HlsCacheSettings> {
  if (
    typeof navigator === "undefined" ||
    !navigator.storage?.estimate ||
    settings.capacityMode !== "auto"
  ) {
    return settings;
  }
  const estimate = await navigator.storage.estimate();
  const quota = estimate.quota || 0;
  const usage = estimate.usage || 0;
  const free = Math.max(0, quota - usage);
  // Use all storage the browser can safely grant except a 10% quota reserve.
  // There is no application-defined GB ceiling; the browser/OS remains final.
  const target = Math.max(
    50 * 1024 * 1024,
    Math.floor(Math.min(quota * 0.9, usage + free * 0.9)),
  );
  return saveHlsCacheSettings({ ...settings, maxCacheBytes: target, maxSegments: 1000000 });
}

export async function registerMobileHlsCache(): Promise<void> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  const basePath = window.location.pathname.startsWith("/tiktok") ? "/tiktok/" : "/";
  const registration = await navigator.serviceWorker.register(
    `${basePath}hls-cache-sw.js?v=${encodeURIComponent(APP_BUILD)}`,
    {
      scope: basePath,
      updateViaCache: "none",
    },
  );
  await registration.update().catch(() => {});
  const settings = await applyAutomaticCacheCapacity(getHlsCacheSettings()).catch(
    () => getHlsCacheSettings(),
  );
  sendCacheSettings();
}

function askWorker<T>(type: string): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error("缓存服务尚未接管页面，请刷新后重试"));
      return;
    }
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => reject(new Error("缓存服务响应超时")), 5000);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      resolve(event.data as T);
    };
    navigator.serviceWorker.controller.postMessage({ type }, [channel.port2]);
  });
}

export async function getHlsCacheStats(): Promise<HlsCacheStats> {
  return askWorker<HlsCacheStats>("HLS_CACHE_STATS");
}

export async function clearHlsCache(): Promise<HlsCacheStats> {
  return askWorker<HlsCacheStats>("HLS_CACHE_CLEAR");
}

export function getResumePosition(videoId: string): number {
  if (typeof window === "undefined" || !getHlsCacheSettings().resumePlayback) return 0;
  try {
    const saved = JSON.parse(window.localStorage.getItem(`${RESUME_PREFIX}${videoId}`) || "null");
    return Number(saved?.position) || 0;
  } catch {
    return 0;
  }
}

export function saveResumePosition(videoId: string, position: number, duration = 0): void {
  if (typeof window === "undefined" || !getHlsCacheSettings().resumePlayback) return;
  if (!Number.isFinite(position) || position < 2 || (duration > 0 && position >= duration - 3)) {
    window.localStorage.removeItem(`${RESUME_PREFIX}${videoId}`);
    return;
  }
  window.localStorage.setItem(
    `${RESUME_PREFIX}${videoId}`,
    JSON.stringify({ position: Math.floor(position * 10) / 10, updatedAt: Date.now() }),
  );
}
